// AGC: SupplyCard - Unified card component for displaying supplies/inputs
// AGC: Used in both LotBOMCard (lot context) and InventoryPage (global view)
// AGC: Clickable to open SupplyDetailModal with movement history and price tracking

import React from 'react'
import { 
  ChevronRight, 
  AlertTriangle, 
  Check, 
  X, 
  Package,
  TrendingUp,
} from 'lucide-react'
import { CubeIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../hooks/useTranslation'
import type { StockStatus, InputCategory } from '../../types/inventory'
import { getInputCategoryInfo } from '../../types/inventory'

// =============================================================================
// AGC: TYPES
// =============================================================================

export interface SupplyCardProps {
  // AGC: Basic data (always present)
  inputId: string
  inputName: string
  category: InputCategory
  unit: string
  currentStock: number
  
  // AGC: Lot context (optional - only in LotBOMCard)
  lotContext?: {
    requiredQuantity: number
    needToBuy: number
    stockStatus: StockStatus
    tasks: string[]
  }
  
  // AGC: Price info (optional)
  costPerUnit?: number
  
  // AGC: Minimum stock threshold (for status when no lot context)
  minimumStock?: number
  
  // AGC: Callbacks
  onClick?: () => void
  onQuickAddStock?: () => void
  
  // AGC: Visual options
  compact?: boolean
  showTasks?: boolean
  animate?: boolean
}

// =============================================================================
// AGC: STATUS BADGE COMPONENT
// =============================================================================

interface StatusBadgeProps {
  status: StockStatus
  size?: 'sm' | 'md'
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'sm' }) => {
  const { t } = useTranslation()
  
  const config: Record<StockStatus, { 
    bg: string
    text: string
    icon: React.ReactNode
    label: string 
  }> = {
    out: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-700 dark:text-red-400',
      icon: <X className="w-3 h-3" />,
      label: t('inventory.stockStatus.out') || 'Esgotado',
    },
    critical: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-600 dark:text-red-400',
      icon: <AlertTriangle className="w-3 h-3" />,
      label: t('inventory.stockStatus.critical') || 'Crítico',
    },
    low: {
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      text: 'text-amber-700 dark:text-amber-400',
      icon: <AlertTriangle className="w-3 h-3" />,
      label: t('inventory.stockStatus.low') || 'Baixo',
    },
    ok: {
      bg: 'bg-emerald-100 dark:bg-emerald-900/30',
      text: 'text-emerald-700 dark:text-emerald-400',
      icon: <Check className="w-3 h-3" />,
      label: t('inventory.stockStatus.ok') || 'OK',
    },
    overstock: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-700 dark:text-blue-400',
      icon: <TrendingUp className="w-3 h-3" />,
      label: t('inventory.stockStatus.overstock') || 'Excesso',
    },
  }
  
  const { bg, text, icon, label } = config[status]
  const sizeClasses = size === 'sm' 
    ? 'px-2 py-0.5 text-xs' 
    : 'px-2.5 py-1 text-sm'
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${bg} ${text} ${sizeClasses}`}>
      {icon}
      <span>{label}</span>
    </span>
  )
}

// =============================================================================
// AGC: PROGRESS BAR COMPONENT
// =============================================================================

interface MiniProgressBarProps {
  current: number
  required: number
  status: StockStatus
}

const MiniProgressBar: React.FC<MiniProgressBarProps> = ({ current, required, status }) => {
  const percentage = required > 0 ? Math.min(100, (current / required) * 100) : 100
  
  const barColors: Record<StockStatus, string> = {
    out: 'bg-red-500',
    critical: 'bg-red-500',
    low: 'bg-amber-500',
    ok: 'bg-emerald-500',
    overstock: 'bg-blue-500',
  }
  
  return (
    <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
      <div 
        className={`h-full rounded-full transition-all duration-300 ${barColors[status]}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  )
}

// =============================================================================
// AGC: MAIN COMPONENT
// =============================================================================

export const SupplyCard: React.FC<SupplyCardProps> = ({
  inputId,
  inputName,
  category,
  unit,
  currentStock,
  lotContext,
  costPerUnit,
  minimumStock = 0,
  onClick,
  onQuickAddStock,
  compact = false,
  showTasks = true,
  animate = false,
}) => {
  const { t } = useTranslation()
  
  // AGC: Get category info for icon and color
  const catInfo = getInputCategoryInfo(category)
  const CategoryIcon = catInfo?.iconComponent || CubeIcon
  const categoryName = catInfo?.namePt || category
  
  // AGC: Determine stock status
  const stockStatus: StockStatus = lotContext?.stockStatus || (() => {
    if (currentStock <= 0) return 'out'
    if (minimumStock > 0 && currentStock < minimumStock * 0.5) return 'critical'
    if (minimumStock > 0 && currentStock < minimumStock) return 'low'
    return 'ok'
  })()
  
  // AGC: Calculate display values
  const requiredQty = lotContext?.requiredQuantity || 0
  const needToBuy = lotContext?.needToBuy || 0
  const tasks = lotContext?.tasks || []
  
  // AGC: Determine card styling based on status
  const getCardClasses = () => {
    let base = 'w-full text-left rounded-xl border transition-all duration-200'
    
    // AGC: Status-based border colors
    if (stockStatus === 'out' || stockStatus === 'critical') {
      base += ' border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
    } else if (stockStatus === 'low') {
      base += ' border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
    } else {
      base += ' border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800'
    }
    
    // AGC: Interactive states
    if (onClick) {
      base += ' cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 active:scale-[0.99]'
    }
    
    // AGC: Animation
    if (animate && (stockStatus === 'out' || stockStatus === 'critical')) {
      base += ' animate-pulse-subtle'
    }
    
    return base
  }
  
  // AGC: Format quantity display
  const formatQty = (qty: number) => {
    if (qty >= 1000) return `${(qty / 1000).toFixed(1)}k`
    if (qty >= 100) return qty.toFixed(0)
    if (qty >= 10) return qty.toFixed(1)
    return qty.toFixed(2)
  }
  
  return (
    <button
      onClick={onClick}
      className={getCardClasses()}
      disabled={!onClick}
    >
      <div className={compact ? 'p-3' : 'p-4'}>
        {/* AGC: Top Row - Icon, Name, Status Badge */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* AGC: Category Icon */}
            <div className={`
              ${compact ? 'w-10 h-10' : 'w-12 h-12'} 
              rounded-xl flex items-center justify-center flex-shrink-0
              ${stockStatus === 'out' || stockStatus === 'critical' 
                ? 'bg-red-100 dark:bg-red-900/30' 
                : stockStatus === 'low'
                ? 'bg-amber-100 dark:bg-amber-900/30'
                : 'bg-gray-100 dark:bg-gray-700'
              }
            `}>
              <CategoryIcon className={compact ? 'w-5 h-5' : 'w-6 h-6'} style={{ color: catInfo?.color }} />
            </div>
            
            {/* AGC: Name and Category */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`font-semibold text-gray-900 dark:text-white truncate ${compact ? 'text-sm' : 'text-base'}`}>
                  {inputName}
                </h3>
                <StatusBadge status={stockStatus} size={compact ? 'sm' : 'sm'} />
              </div>
              
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {categoryName}
                {showTasks && tasks.length > 0 && (
                  <span className="ml-1">
                    • {tasks.slice(0, 2).join(', ')}
                    {tasks.length > 2 && ` +${tasks.length - 2}`}
                  </span>
                )}
              </p>
            </div>
          </div>
          
          {/* AGC: Chevron indicator */}
          {onClick && (
            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
          )}
        </div>
        
        {/* AGC: Stock Info Row */}
        <div className={`${compact ? 'mt-2' : 'mt-3'}`}>
          {/* AGC: With lot context - show required vs current */}
          {lotContext ? (
            <>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Stock: </span>
                    <span className={`font-semibold ${
                      stockStatus === 'out' || stockStatus === 'critical' 
                        ? 'text-red-600 dark:text-red-400'
                        : stockStatus === 'low'
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {formatQty(currentStock)} {unit}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">/ </span>
                    <span className="text-gray-600 dark:text-gray-300">
                      {formatQty(requiredQty)} {unit}
                    </span>
                  </div>
                </div>
                
                {/* AGC: Need to buy indicator */}
                {needToBuy > 0 ? (
                  <span className="text-red-600 dark:text-red-400 font-medium text-xs">
                    {t('supplies.missing') || 'Faltam'} {formatQty(needToBuy)} {unit}
                  </span>
                ) : currentStock > requiredQty ? (
                  <span className="text-blue-600 dark:text-blue-400 font-medium text-xs">
                    {t('supplies.excess') || 'Excesso'} {formatQty(currentStock - requiredQty)} {unit}
                  </span>
                ) : (
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium text-xs">
                    ✓ OK
                  </span>
                )}
              </div>
              
              {/* AGC: Progress bar */}
              <MiniProgressBar 
                current={currentStock} 
                required={requiredQty} 
                status={stockStatus}
              />
            </>
          ) : (
            /* AGC: Without lot context - simple stock display */
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400 text-sm">Stock:</span>
                <span className={`font-bold text-lg ${
                  stockStatus === 'out' || stockStatus === 'critical' 
                    ? 'text-red-600 dark:text-red-400'
                    : stockStatus === 'low'
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {formatQty(currentStock)}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">{unit}</span>
              </div>
              
              {costPerUnit && costPerUnit > 0 && (
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  R$ {costPerUnit.toFixed(2)}/{unit}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </button>
  )
}

export default SupplyCard
